import { updateFormConditions } from "../components/Form/services/form.service";
import { isLocalhost } from "../utils/helpers";
import splitApi, { getInvalidationTags } from "./emptySplitApi";

const licensesApi = splitApi.injectEndpoints({
  endpoints: (build) => ({
    licenses: build.query({
      query: (body) => ({
        url: "api/licenses",
        method: "POST",
        body,
      }),
      transformResponse(baseQueryReturnValue) {
        const { pages, total, limit, page } = baseQueryReturnValue;
        // eslint-disable-next-line no-param-reassign
        baseQueryReturnValue.pagination = { pages, total, limit, page };
        return baseQueryReturnValue;
      },
      providesTags: ["Licenses"],
    }),
    subLicenses: build.mutation({
      query: (body) => ({
        url: "api/licenses/subLicenses",
        method: "POST",
        body,
      }),
      providesTags: ["Licenses"],
    }),
    licensesConfig: build.query({
      query: () => ({
        url: "/dist/config/licenses.json",
      }),
      providesTags: ["License"],
    }),
    getApprovedLicenses: build.mutation({
      query: (params) => ({
        url: "api/licenses",
        method: "GET",
        params,
      }),
    }),
    getRecord: build.query({
      query: ({ recordType, licenseNumber, params }) => ({
        url: `api/licenses/${recordType}/${licenseNumber}`,
        params,
      }),
      providesTags: ["License"],
    }),
    license: build.mutation({
      query: ({ licenseType, licenseNumber, params }) => ({
        url: `api/licenses/${licenseType}/${licenseNumber}`,
        method: "GET",
        params,
      }),
    }),
    licenseLookup: build.mutation({
      query: ({ licenseType, licenseNumber, params }) => ({
        url: `api/licenses/public/${licenseType}/${licenseNumber}`,
        method: "GET",
        params,
      }),
    }),
    linkedLicenses: build.query({
      query: (params) => ({
        url: "api/licenses/linkedLicenses",
        params,
      }),
      providesTags: ["License"],
    }),
    metrcConfig: build.query({
      query: () => ({
        url: "/dist/config/metrc.config.json",
      }),
    }),
    metrcInfo: build.mutation({
      query: ({ licenseType, licenseNumber, params }) => ({
        url: `api/metrc/licenses/${licenseType}/${licenseNumber}/status`,
        method: "GET",
        params,
      }),
    }),
    formConfig: build.query({
      query: (params) => ({
        url: "api/config/formConfig",
        params,
      }),
      transformResponse: async (baseQueryReturnValue) => {
        if (isLocalhost && process.env.REACT_APP_TEST_LICENSE_FORM) {
          try {
            const response = await fetch(
              process.env.REACT_APP_TEST_LICENSE_FORM
            );
            const externalForm = await response.json();
            return updateFormConditions(externalForm);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
              `API CALL FAILED: ${process.env.REACT_APP_TEST_LICENSE_FORM}`,
              error
            );
            return baseQueryReturnValue;
          }
        }
        return baseQueryReturnValue;
      },
      providesTags: ["License"],
    }),
    updateLicenseState: build.mutation({
      query: ({ licenseType, licenseNumber, type, params, body }) => ({
        url: `api/licenses/${licenseType}/${licenseNumber}/${type}`,
        method: "PUT",
        params,
        body,
      }),
      invalidatesTags: getInvalidationTags(),
    }),
    associatedLicenseConfig: build.query({
      query: () => ({
        url: "/dist/config/associatedLicenseWidget.config.json",
      }),
    }),
    licenseBannerSchemaConfig: build.query({
      query: () => ({
        url: "/dist/config/license.bannerSchema.config.json",
      }),
    }),
    getAssociatedLicenses: build.query({
      query: ({
        licenseType,
        businessLicenseNumber,
        associationType,
        ...body
      }) => ({
        url: `api/licenses/${licenseType}/${businessLicenseNumber}/association/${associationType}`,
        method: "POST",
        body,
      }),
      transformResponse(baseQueryReturnValue) {
        const { pages, total, limit, page } = baseQueryReturnValue;
        // eslint-disable-next-line no-param-reassign
        baseQueryReturnValue.pagination = { pages, total, limit, page };
        return baseQueryReturnValue;
      },
      providesTags: ["AssociatedLicenses"],
    }),
  }),
});

export const {
  useLicensesQuery,
  useLazyLicensesQuery,
  useSubLicensesMutation,
  useLicensesConfigQuery,
  useGetApprovedLicensesMutation,
  useGetRecordQuery,
  useLicenseMutation,
  useLicenseLookupMutation,
  useLinkedLicensesQuery,
  useMetrcConfigQuery,
  useMetrcInfoMutation,
  useFormConfigQuery,
  useUpdateLicenseStateMutation,
  useAssociatedLicenseConfigQuery,
  useGetAssociatedLicensesQuery,
  useLicenseBannerSchemaConfigQuery,
} = licensesApi;
